import { FC, useEffect, useRef, useState } from 'react';
import {
    Grid,
    Typography,
    Chip,
    Button,
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store';
import {
    toggleResistanceMove,
    removeResistanceMove,
    clearResistanceMoves,
    closeResistanceMovesPanel,
} from '../../../slices/stakeholderSlice';
import { moveGetAll } from '../../../api/move';
import { InputField } from '../../input-field';
import { openMoveDialog } from '../../../slices/moveSlice';

interface ResistanceMovesPanelProps {
    height: number;
}

// Tactical actions addressing one resistance. Same interaction as the Factor
// preventive/contingency panels — create a new action inline or attach existing
// ones, listed as removable chips — but scoped to the resistance row the user
// opened it from (state.stakeholder.activeResistanceIndex).
export const ResistanceMovesPanel: FC<ResistanceMovesPanelProps> = ({ height }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { stakeholderLevers, activeResistanceIndex } = useSelector((state: RootState) => state.stakeholder);
    const { moveDialogOpen } = useSelector((state: RootState) => state.move);

    const resistance = activeResistanceIndex === null
        ? null
        : stakeholderLevers.resistances[activeResistanceIndex];
    const selectedMoves = resistance?.selectedMoves || [];

    const [moves, setMoves] = useState([]);
    const [total, setTotal] = useState(0);
    const [searchedString, setSearchedString] = useState('');

    const getMoves = async () => {
        try {
            const res = await moveGetAll('name', 'asc', searchedString, 'all', 1, 1000);
            if (res.items) {
                setMoves(res.items);
                setTotal(res.total);
            } else {
                setMoves([]);
                setTotal(0);
            }
        } catch (error) {
            console.error('Error fetching actions:', error);
            setMoves([]);
            setTotal(0);
        }
    };

    useEffect(() => {
        getMoves();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchedString]);

    // Refresh once the shared New Action dialog closes, so a just-created action
    // is attachable straight away (same as the Factor panels).
    const wasMoveDialogOpenRef = useRef(false);
    useEffect(() => {
        if (wasMoveDialogOpenRef.current && !moveDialogOpen) {
            getMoves();
        }
        wasMoveDialogOpenRef.current = moveDialogOpen;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moveDialogOpen]);

    if (!resistance) {
        return null;
    }

    return (
        <Grid item xs={4} sx={{ backgroundColor: 'rgba(255, 123, 0, 0.25)', overflowY: 'scroll' }} padding={0} spacing={2} height={height}>
            <Grid container spacing={2} padding={4} sx={{ paddingLeft: 2 }}>
                <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h5" style={{ color: 'rgb(255, 123, 0)' }}>
                            {t('select_resistance_actions')}
                            <Chip
                                color="default"
                                label={`${selectedMoves.length}/${total}`}
                                size="small"
                                variant="filled"
                                sx={{ ml: 1 }}
                            />
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => dispatch(closeResistanceMovesPanel())}
                            sx={{ minWidth: 'auto', px: 2, py: 0.5, fontSize: '0.75rem' }}
                        >
                            {t('Hide')}
                        </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        {resistance.resistance?.description || ''}
                    </Typography>
                </Grid>

                <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                    <Button
                        variant="contained"
                        size="small"
                        fullWidth
                        onClick={() => dispatch(openMoveDialog({ element: { id: null }, isEditabled: true, isDuplicated: false }))}
                        style={{ backgroundColor: 'rgb(255, 123, 0)' }}
                    >
                        {t('New action')}
                    </Button>
                </Grid>

                {selectedMoves.length > 0 && (
                    <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                        <Box sx={{ backgroundColor: 'rgba(255, 123, 0, 0.1)', borderRadius: 1, p: 1, mb: 1 }}>
                            <Typography variant="body2" color="warning.main" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {t('selected_resistance_actions')} ({selectedMoves.length})
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selectedMoves.map((move) => (
                                    <Chip
                                        key={move.id}
                                        label={move.name}
                                        size="small"
                                        onDelete={() => dispatch(removeResistanceMove({ index: activeResistanceIndex, moveId: move.id }))}
                                        color="warning"
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                            <Button
                                size="small"
                                color="error"
                                onClick={() => dispatch(clearResistanceMoves(activeResistanceIndex))}
                                sx={{ mt: 1 }}
                            >
                                {t('Clear All')}
                            </Button>
                        </Box>
                    </Grid>
                )}

                <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                    <InputField
                        fullWidth
                        placeholder={t('Search actions...')}
                        name="search"
                        onChange={(e) => setSearchedString(e.target.value)}
                        type="text"
                        value={searchedString}
                    />
                </Grid>

                <Grid item xs={12}>
                    <List>
                        {moves.map((move) => {
                            const isSelected = selectedMoves.some((selected) => selected.id === move.id);
                            return (
                                <ListItem disablePadding key={move.id} sx={{ marginBottom: 1 }}>
                                    <ListItemButton
                                        onClick={() => dispatch(toggleResistanceMove(move))}
                                        selected={isSelected}
                                        sx={{
                                            borderRadius: 1,
                                            backgroundColor: isSelected ? 'rgba(255, 123, 0, 0.1)' : 'background.default',
                                            '&.Mui-selected': {
                                                backgroundColor: 'rgba(255, 123, 0, 0.2)',
                                                '&:hover': { backgroundColor: 'rgba(255, 123, 0, 0.3)' },
                                            },
                                        }}
                                    >
                                        <ListItemText
                                            primary={move.name}
                                            secondary={move.description}
                                            primaryTypographyProps={{ variant: 'body2', fontWeight: isSelected ? 'bold' : 'normal' }}
                                            secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default ResistanceMovesPanel;
